import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import SectionProgress from '~/pages/anatomyV2/libs/components/SectionProgress'
import { Cohort } from '~/shared/constants/commonConstants'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { selectCohortID } from '~/shared/store/common/selects'
import { TPageProps } from '~/shared/types/commonTypes'
import { shuffleArray } from '~/shared/utils'

import { Styles as S } from './styles'

const SECTION_NUMBER = 6

const OPTION_KEYS = [
  { key: 'history', i18nKey: 'first' },
  { key: 'art', i18nKey: 'second' },
  { key: 'math', i18nKey: 'third' },
  { key: 'psychology', i18nKey: 'fourth' },
  { key: 'communication', i18nKey: 'fifth' },
  { key: 'biology', i18nKey: 'sixth' },
  { key: 'ai', i18nKey: 'seventh' },
  { key: 'geography', i18nKey: 'eighth' },
  { key: 'space', i18nKey: 'ninth' },
  { key: 'finance', i18nKey: 'tenth' },
  { key: 'parenting', i18nKey: 'eleventh' },
  { key: 'physics', i18nKey: 'twelfth' },
] as const

const TopicsAnatomy = ({ nextPagePath, pageId }: TPageProps) => {
  const { t } = useTranslation('anatomy')
  const cohort = useSelector(selectCohortID)
  const isAnatomyV2 = cohort === Cohort.ANATOMY_V2
  const [answers, setAnswers] = useState<string[]>([])
  const [shuffledOptions] = useState(() => shuffleArray(OPTION_KEYS))

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: 'Pick the topics you like',
  })

  const toggleOption = (key: string) => {
    setAnswers((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  }

  return (
    <S.Root>
      <S.StyledContainer>
        {isAnatomyV2 && (
          <SectionProgress currentSection={SECTION_NUMBER} pageId={pageId} />
        )}

        <S.Title $isAnatomyV2={isAnatomyV2}>
          <Trans
            ns="anatomy"
            i18nKey="onboarding.multipleChoices.topicsAnatomy.title"
          />
        </S.Title>

        <S.Subtitle>
          <Trans
            ns="anatomy"
            i18nKey="onboarding.multipleChoices.topicsAnatomy.subtitle"
          />
        </S.Subtitle>

        <S.ScrollContent>
          <S.Grid>
            {shuffledOptions.map(({ key, i18nKey }) => {
              const isSelected = answers.includes(key)

              return (
                <S.TopicOption
                  key={key}
                  type="button"
                  $isSelected={isSelected}
                  $isAnatomyV2={isAnatomyV2}
                  onClick={() => toggleOption(key)}
                >
                  <S.Value>
                    {t(
                      `onboarding.multipleChoices.topicsAnatomy.options.${i18nKey}`,
                    )}
                  </S.Value>
                </S.TopicOption>
              )
            })}
          </S.Grid>
        </S.ScrollContent>

        <S.ButtonContainer>
          <S.StyledButton
            type="button"
            onClick={() => handleContinue(answers)}
            disabled={!answers.length}
          >
            <Trans i18nKey="actions.continue" />
          </S.StyledButton>
        </S.ButtonContainer>
      </S.StyledContainer>
    </S.Root>
  )
}

export default TopicsAnatomy
